import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { injectIntl, useIntl } from "react-intl";
import { connect } from "react-redux";
import { Redirect } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation } from "@apollo/client";
import { TextField, Button } from "@material-ui/core";
import { useFormik } from "formik";

import { AlertMessage } from "../../../Components/AlertMessage";
import { SectionLoader } from "../../../Components/SectionLoader";
import * as auth from "../_redux/authRedux";
import {
  SEND_FORGET_PASSWORD_LINK,
  SEND_COMPLETE_PROFILE_LINK,
} from "../mutations/index";

import { useForgotPasswordValidationSchema } from "./useForgotPasswordValidationSchema";

function ForgotPassword(props) {
  const { initialValues, forgotPasswordValidationSchema } = useForgotPasswordValidationSchema();

  const { show, setShow } = props;

  const [showLoader, setShowLoader] = useState(false);

  const [isLogin, setGoToLogin] = useState(false);

  const [forgotPwdError, setForgotPwdError] = useState(false);

  const [state, setState] = useState({
    open: false,
    message:
      "We have sent a password reset link on your registered email address. Click the link to reset your password.",
    severity: "success",
  });

  const intl = useIntl();

  const [
    sendForgetLink,
    { loading: pwdLoading,
      error: pwdError,
      data: pwdData,
    }] = useMutation(SEND_FORGET_PASSWORD_LINK, {
    errorPolicy: "all",
  });

  const [
    sendCompleteProfileLink,
    {
      loading: completeProfileLinkLoading,
      error: completeProfileLinkError,
      data: completeProfileLinkData,
    },
  ] = useMutation(SEND_COMPLETE_PROFILE_LINK);

  useEffect(() => {
    if (pwdLoading) {
      setShowLoader(true);
      formik.setStatus("");
    }

    if (pwdError) {
      setShowLoader(false);
      let errorTxt = pwdError.toString().replace("Error: Error:", "");

      if (
        errorTxt.trim() === intl.formatMessage({ id: "FORGOT_PWD_ERRORTEXT" })
      ) {
        setForgotPwdError(true);
        toast.error(
          intl.formatMessage({
            id: "FORGOT_PWD_ERRORTEXT",
          }),
          {
            position: toast.POSITION.TOP_RIGHT,
          },
        );
      } else {
        // formik.setStatus(pwdError.toString().replace("Error: Error:", ""));
        formik.setStatus(pwdError?.networkError?.result?.errors[0]?.message?.toString() || 
          pwdError.toString().replace("Error: Error:", ""));
      }
    }

    if (pwdData) {
      toast.success(
        intl.formatMessage({
          id: "GLOBAL.PWD_RESENTLINK",
        }),
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
      setShowLoader(false);
      setState({ ...state, open: true });
      handleModelClose();
    }
  }, [pwdData, pwdError, pwdLoading]);

  useEffect(() => {
    if (completeProfileLinkLoading) {
      setShowLoader(true);
    }

    if (completeProfileLinkError) {
      setShowLoader(false);
      formik.setStatus(pwdError.toString().replace("Error: Error:", ""));
    }

    if (completeProfileLinkData) {
      toast.success(
        intl.formatMessage({
          id: "GLOBAL.PWD_RESENTLINK",
        }),
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
      setShowLoader(false);
      setForgotPwdError(false);
      setState({ ...state, open: true });
      handleModelClose();
    }
  }, [
    completeProfileLinkLoading,
    completeProfileLinkError,
    completeProfileLinkData,
  ]);

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordValidationSchema,
    onSubmit: (values, { setSubmitting }) => {
      sendForgetLink({
        variables: {
          email: values.email,
        },
      });
      setSubmitting(false);
    },
  });

  const handleSnackbarClose = () => {
    setState({ ...state, open: false });
  };

  const handleChange = e => {
    formik.setFieldValue("email", e.target.value);
    formik.setStatus(null);
  };

  const handleModelClose = () => {
    setShow(false);
  };

  return (
    <>
      <SectionLoader loader={showLoader} />
      <Modal
        show={show}
        onHide={handleModelClose}
        aria-labelledby="example-modal-sizes-title-lg"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Forgotten Password ?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLogin && <Redirect to="/auth/login" />}

          <div
            className="login-form login-forgot mt-5"
            style={{ display: "block" }}
          >
            <AlertMessage
              message={state.message}
              severity={state.severity}
              handleClose={handleSnackbarClose}
              open={state.open}
            />
            <div>
              <form
                onSubmit={formik.handleSubmit}
                className="form fv-plugins-bootstrap fv-plugins-framework animated animate__animated animate__backInUp"
              >
                {formik.status && (
                  <div className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                    <div className="alert-text font-weight-bold">
                      {formik.status}
                    </div>
                  </div>
                )}

                <div className="form-group fv-plugins-icon-container">
                  <label className="form-label">
                    Enter your email to reset your password
                  </label>

                  <TextField
                    fullWidth
                    name="email"
                    variant="outlined"
                    disabled={forgotPwdError}
                    value={formik.values.email}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    onChange={e => {
                      handleChange(e);
                    }}
                  />
                </div>
                <div className="modal-footer">
                  <Button
                    id="kt_login_signin_submit"
                    type="submit"
                    disabled={formik.isSubmitting || forgotPwdError}
                    className={"btn btn-primary"}
                  >
                    <span>Submit</span>
                  </Button>
                  {forgotPwdError && (
                    <button
                      id="kt_login_signin_submit"
                      type="button"
                      className={
                        "btn btn-outline-secondary bg-white font-weight-bold px-9 py-4 my-3 w-100"
                      }
                      onClick={() => {
                        sendCompleteProfileLink({
                          variables: {
                            email: formik?.values?.email,
                          },
                        });
                      }}
                    >
                      <span>Resend Complete Profile Link</span>
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default injectIntl(connect(null, auth.actions)(ForgotPassword));
